import React, { useEffect, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  Platform,
  Pressable,
  PressableProps,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { Tooltip as TooltipComponent } from 'react-native-popper';

type TooltipComponentProps = React.ComponentProps<typeof TooltipComponent>;
type Placement = TooltipComponentProps['placement'];
type Offset = TooltipComponentProps['offset'];

interface TooltipProps {
  children: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  /** When provided, overrides the internal hover state to control tooltip visibility programmatically. */
  isOpen?: boolean;
  offset?: { x?: Offset; y?: Offset };
  placement?: Placement;
  tabIndex?: PressableProps['tabIndex'];
  trigger: React.ReactNode;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  containerStyle,
  isOpen,
  offset,
  placement = 'top',
  tabIndex,
  trigger,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [styles] = useStyles();

  const isVisible = (isOpen ?? isHovered) && !isDismissed;

  useEffect(() => {
    if (!isOpen) {
      setIsDismissed(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isVisible || Platform.OS !== 'web') {
      return;
    }

    const dismiss = () => {
      setIsHovered(false);
      setIsDismissed(true);
    };

    window.addEventListener('scroll', dismiss, { capture: true });
    return () =>
      window.removeEventListener('scroll', dismiss, { capture: true });
  }, [isVisible]);

  return (
    <TooltipComponent
      crossOffset={offset?.x}
      isOpen={isVisible}
      offset={offset?.y}
      onOpenChange={setIsHovered}
      placement={placement}
      trigger={
        <Pressable
          onHoverIn={() => {
            if (isOpen === undefined) {
              setIsHovered(true);
            }
          }}
          onHoverOut={() => {
            if (isOpen === undefined) {
              setIsHovered(false);
            }
          }}
          tabIndex={tabIndex}
        >
          <View>{trigger}</View>
        </Pressable>
      }
    >
      {Platform.OS !== 'web' && (
        <TooltipComponent.Backdrop onPress={() => setIsDismissed(true)} />
      )}
      <TooltipComponent.Content>
        <View style={[styles.tooltipContainer, containerStyle]}>
          {children}
        </View>
      </TooltipComponent.Content>
    </TooltipComponent>
  );
};

const useStyles = createStyles((theme) => ({
  tooltipContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    padding: theme.sk.spacing.spaceMedium,
  },
}));
